/**
 * Active Organization Context
 *
 * Manages the currently active organization for superadmin users.
 * Allows switching between organizations and persists the selection.
 *
 * Non-superadmin users always see their own organization.
 * Superadmin users can switch to view other organizations.
 */

import { createContext, useContext, ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import StorageService from '@/utils/storage';
import { StorageKey, SessionKey } from '@/types/storage';
import { asyncActionManager } from '@/business/utils/async-action-manager';

interface ActiveOrgContextValue {
  activeOrganizationId: string;
  setActiveOrganizationId: (orgId: string) => void;
  isViewingDifferentOrg: boolean;
  returnToMyOrg: () => void;
}

const ActiveOrgContext = createContext<ActiveOrgContextValue | undefined>(
  undefined
);

interface ActiveOrgProviderProps {
  children: ReactNode;
}

export function ActiveOrgProvider({ children }: ActiveOrgProviderProps) {
  const { user } = useAuth();

  // Derived on every render — always correct because org switch triggers
  // window.location.reload(), making this value stable within a page lifetime.
  const activeOrganizationId = user?.organization?.is_superadmin_org
    ? (StorageService.get<string>(StorageKey.ACTIVE_ORGANIZATION) ??
      user.organization_id ??
      '')
    : (user?.organization_id ?? '');

  // Registered on every render (idempotent). Same pattern as useAuth's
  // httpClient.setTokenProvider — side effect intentionally in render body
  // so children's first render already sees the correct org.
  asyncActionManager.setOrganizationIdProvider(() => activeOrganizationId);

  const setActiveOrganizationId = (orgId: string) => {
    if (!user?.organization?.is_superadmin_org) return;
    StorageService.set(StorageKey.ACTIVE_ORGANIZATION, orgId);
    sessionStorage.removeItem(SessionKey.LB_TAB);
    StorageService.set(StorageKey.SIDEBAR_STATE, true);
    window.location.reload();
  };

  const returnToMyOrg = () => {
    if (!user?.organization_id) return;
    StorageService.remove(StorageKey.ACTIVE_ORGANIZATION);
    window.location.reload();
  };

  const isViewingDifferentOrg =
    user?.organization?.is_superadmin_org === true &&
    activeOrganizationId !== user?.organization_id;

  return (
    <ActiveOrgContext.Provider
      value={{
        activeOrganizationId,
        setActiveOrganizationId,
        isViewingDifferentOrg,
        returnToMyOrg,
      }}
    >
      {children}
    </ActiveOrgContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- context+hook co-location pattern
export function useActiveOrganization(): ActiveOrgContextValue {
  const context = useContext(ActiveOrgContext);

  if (context === undefined) {
    throw new Error(
      'useActiveOrganization must be used within ActiveOrgProvider'
    );
  }

  return context;
}
